/* eslint-disable prettier/prettier */
/* eslint-disable no-nested-ternary */
import React from 'react';
import {
  Box,
  Flex,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Badge,
} from '@chakra-ui/react';
import { Eye, ChevronUp, ChevronDown } from 'lucide-react';
import { ICompany } from '../../../../../hooks/useCompanies';
import {
  ITaxAnalysisSNCompany,
  formatarEmReais,
  formatarPercentual,
} from '../../../../../types/taxAnalysis';
import { formatCNPJ } from '../../../../../utils/FormatFunctions';

interface SortableThProps {
  field: string;
  label: string;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  // eslint-disable-next-line no-unused-vars
  onSort: (field: string) => void;
  isNumeric?: boolean;
  textAlign?: 'center' | 'left' | 'right';
  w?: string;
}

function SortableTh({
  field,
  label,
  orderBy,
  orderDirection,
  onSort,
  isNumeric,
  textAlign,
  w,
}: SortableThProps) {
  const isActive = orderBy === field;
  return (
    <Th
      cursor="pointer"
      onClick={() => onSort(field)}
      isNumeric={isNumeric}
      textAlign={textAlign}
      w={w}
      _hover={{ bg: 'gray.100' }}
      userSelect="none"
    >
      <Flex
        align="center"
        justify={isNumeric ? 'flex-end' : textAlign === 'center' ? 'center' : 'flex-start'}
        gap={1}
      >
        <Text as="span">{label}</Text>
        {isActive &&
          (orderDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
      </Flex>
    </Th>
  );
}

interface TaxTableSNProps {
  companies: ICompany[];
  companiesWithTax: ITaxAnalysisSNCompany[];
  isLoading: boolean;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  // eslint-disable-next-line no-unused-vars
  onRowClick: (company: ICompany) => void;
  // eslint-disable-next-line no-unused-vars
  onSort: (field: string) => void;
}

export function TaxTableSN({
  companies,
  companiesWithTax,
  isLoading,
  orderBy,
  orderDirection,
  onRowClick,
  onSort,
}: TaxTableSNProps) {
  if (isLoading) {
    return (
      <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
        <Flex justify="center" align="center" h="400px">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      </Box>
    );
  }

  if (companies?.length === 0) {
    return (
      <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
        <Flex justify="center" align="center" h="400px">
          <Text color="gray.500">Nenhuma empresa encontrada</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <SortableTh
                field="id"
                label="ID"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
                w="60px"
              />
              <SortableTh
                field="nome"
                label="Empresa"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
              />
              <SortableTh
                field="faturamento"
                label="Faturamento"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
                isNumeric
              />
              <Th isNumeric>Fat. Acumulado</Th>
              <SortableTh
                field="vl_saida"
                label="Saída"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
                isNumeric
              />
              <SortableTh
                field="vl_servico"
                label="Serviço"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
                isNumeric
              />
              <SortableTh
                field="simples_nacional"
                label="Valor SN"
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={onSort}
                isNumeric
              />
              <Th isNumeric>% SN</Th>
              <Th textAlign="center">Anexo</Th>
              <Th textAlign="center">Tabela</Th>
              <Th textAlign="center">Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {companiesWithTax.map((item, index) => (
              <Tr
                key={item.id}
                _hover={{ bg: 'gray.50' }}
                cursor="pointer"
                onClick={() => onRowClick(companies[index])}
              >
                <Td>
                  <Text fontSize="xs" color="gray.600" fontWeight="medium">
                    {item.external_id}
                  </Text>
                </Td>
                <Td>
                  <Box>
                    <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                      {item.razao_social?.length > 30
                        ? `${item.razao_social.substring(0, 30)}...`
                        : item.razao_social}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {formatCNPJ(item.cnpj)}
                    </Text>
                  </Box>
                </Td>
                <Td isNumeric>
                  <Text fontSize="sm">{formatarEmReais(item.total_faturamento)}</Text>
                </Td>
                <Td isNumeric>
                  <Text fontSize="sm" color="blue.600">
                    {formatarEmReais(item.faturamento_acumulado)}
                  </Text>
                </Td>
                <Td isNumeric>
                  <Text fontSize="sm" color="gray.600">
                    {formatarEmReais(item.vl_saida)}
                  </Text>
                </Td>
                <Td isNumeric>
                  <Text fontSize="sm" color="purple.600">
                    {formatarEmReais(item.vl_servico)}
                  </Text>
                </Td>
                <Td isNumeric>
                  <Text fontSize="sm" fontWeight="semibold" color="green.600">
                    {formatarEmReais(item.vl_simples_nacional)}
                  </Text>
                </Td>
                <Td isNumeric>
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={
                      item.simples_nacional_percentual > 15
                        ? 'red.500'
                        : item.simples_nacional_percentual > 10
                          ? 'orange.500'
                          : 'green.500'
                    }
                  >
                    {formatarPercentual(item.simples_nacional_percentual)}
                  </Text>
                </Td>
                <Td textAlign="center">
                  <Badge colorScheme="purple" fontSize="xs">
                    {item.anexo_simples_nacional ?? '-'}
                  </Badge>
                </Td>
                <Td textAlign="center">
                  <Badge colorScheme="green" fontSize="xs">
                    {item.tabela_simples_nacional ?? '-'}
                  </Badge>
                </Td>
                <Td textAlign="center">
                  <IconButton
                    aria-label="Ver detalhes"
                    icon={<Eye size={18} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    onClick={e => {
                      e.stopPropagation();
                      onRowClick(companies[index]);
                    }}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}
